/**
 * 商品 API 模块
 * 负责所有与商品相关的后端 API 调用
 *
 * 设计原则：
 * 1. 单一职责：只处理商品相关的 API 调用
 * 2. 纯函数：不修改外部状态，只返回数据
 * 3. 错误处理：统一处理网络错误和 API 错误
 */

import { API_ENDPOINTS, fetchWithRetry, getApiUrl, PRODUCT_TYPE } from '../config/api';

/**
 * 构建商品列表请求的请求头
 * @param {Object} user - 当前用户信息，包含 token, id, playerid
 * @returns {Object} 请求头对象
 */
const buildProductListHeaders = (user) => {
    // 检查用户数据完整性
    if (!user) {
        throw new Error('User not authenticated');
    }

    // 固定使用 gameToken
    const token = user.gameToken;
    if (!token) {
        throw new Error('Token is empty');
    }

    return {
        'Content-Type': 'application/json',
        Gametoken: token,
        Fpid: user.id,
        Playerid: user.playerid,
    };
};

/**
 * 构建商品列表请求的请求体
 * @param {number[]} productTypes - 要获取的商品类型数组
 * @returns {Object} 请求体对象
 */
const buildProductListBody = (
    productTypes = [
        PRODUCT_TYPE.NEW_STORE_COIN, // 1100 - 金币促销商品
        PRODUCT_TYPE.BASE_COIN, // 1101 - 金币基准商品
        PRODUCT_TYPE.NEW_STORE_GEM, // 1200 - 钻石促销商品
        PRODUCT_TYPE.BASE_GEM, // 1201 - 钻石基准商品
    ],
) => {
    return {
        producttypearrList: productTypes,
    };
};

/**
 * 获取商品列表
 *
 * 调用后端 API 获取商品数据，包括金币、钻石、道具等。
 * 需要用户已登录并携带有效的 token。
 *
 * @param {Object} user - 当前用户信息
 * @param {number[]} [productTypes] - 可选，指定要获取的商品类型
 * @returns {Promise<Object>} 返回解析后的 API 响应数据
 *   - Products: 商品数组
 *   - Pay: 支付相关信息
 * @throws {Error} 当用户未登录、token 为空或 API 调用失败时抛出错误
 *
 */
export const fetchProductList = async (user, productTypes) => {
    // 构建请求头和请求体
    const headers = buildProductListHeaders(user);
    const body = buildProductListBody(productTypes);

    // 发送请求
    const response = await fetchWithRetry(getApiUrl(API_ENDPOINTS.GET_PRODUCT_LIST), {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
    });

    // 检查响应状态
    if (!response.ok) {
        throw new Error(`Failed to fetch products: HTTP ${response.status}`);
    }

    // 解析响应数据
    const data = await response.json();

    // 检查业务错误码
    if (data.Code !== undefined && data.Code !== 0 && data.Code !== 200) {
        throw new Error(`API error: Code ${data.Code}, Message: ${data.Message || 'Unknown'}`);
    }

    // console.error('[productApi] Raw product list:', data);

    return data;
};

/**
 * 默认导出的 API 对象
 * 便于批量导入和使用
 */
export default {
    fetchProductList,
};
